import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IRequirementAuthoringOptions,
  IRequirementNoteDetail,
  RequirementNoteRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ensureOk from "./ensureOk";
import { noteTypeLabelKey } from "./lookupEnums";

type NoteValues = {
  noteTypeId?: string;
  title?: string;
  content?: string;
};

const { Form } = withTypes<NoteValues>();

/** Mirrors RequirementSetNotes.Title. */
const TITLE_MAX_LENGTH = 250;

type NoteDialogProps = {
  requirementSetId: string;
  /** Set for a note on a requirement; absent for a note on the set itself. */
  requirementId?: string;
  note?: IRequirementNoteDetail;
  options?: IRequirementAuthoringOptions;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const NoteDialog = ({
  requirementSetId,
  requirementId,
  note,
  options,
  open,
  onClose,
  onSaved,
}: NoteDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementSetService } = useGlobalContext();

  const validate = (values: NoteValues) => {
    const errors: { [key in keyof NoteValues]?: string } = {};

    if (!values.noteTypeId) errors.noteTypeId = t({ id: "requiredLabel" });
    if (!values.title?.trim()) errors.title = t({ id: "requiredLabel" });
    if (!values.content?.trim()) errors.content = t({ id: "requiredLabel" });

    return errors;
  };

  const handleSubmit = async (values: NoteValues) => {
    try {
      const response = await requirementSetService.saveRequirementNote(
        RequirementNoteRequest.fromJS({
          id: note?.id,
          requirementSetId,
          requirementId,
          noteTypeId: values.noteTypeId,
          title: values.title?.trim(),
          content: values.content?.trim(),
        })
      );

      ensureOk(response);
      onSaved();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={{
          noteTypeId: note?.noteTypeId ?? options?.noteTypes?.[0]?.id ?? "",
          title: note?.title ?? "",
          content: note?.content ?? "",
        }}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} noValidate>
            <DialogTitle>{t({ id: note ? "editNote" : "addNote" })}</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="noteTypeId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        select
                        required
                        variant="standard"
                        fullWidth
                        label={t({ id: "noteType" })}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed && meta.error}
                        {...input}
                      >
                        {(options?.noteTypes ?? []).map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {noteTypeLabelKey(option.enumValue)
                              ? t({ id: noteTypeLabelKey(option.enumValue) })
                              : option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                </Field>

                <Field name="title">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        required
                        variant="standard"
                        fullWidth
                        label={t({ id: "titleLabel" })}
                        inputProps={{ maxLength: TITLE_MAX_LENGTH }}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed && meta.error}
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="content">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        required
                        variant="standard"
                        fullWidth
                        multiline
                        rows={4}
                        label={t({ id: "contentLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed && meta.error}
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{submitError}</Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disabled={submitting} onClick={onClose}>
                {t({ id: "cancelButton" })}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {t({ id: note ? "saveButton" : "addNote" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default NoteDialog;
